/**
 * Office.js initialization utilities for the Excel Add-in.
 *
 * Office.js is loaded via a `<script>` tag in the add-in layout.  These
 * helpers provide typed access to Office APIs and a React-friendly
 * initialization hook.
 *
 * IMPORTANT: The add-in must run in WebView2 (Edge Chromium) so that the
 * Next.js App Router has full History API support.  Force it with:
 *   npx office-addin-dev-settings webview apps/office-addin/manifest.xml edge-chromium
 */

import type { OfficeAddinSurface } from "@workspace/services/domain";

export type OfficeReadyInfo =
  | {
      status: "ready";
      host: string | null;
      platform: string | null;
      /**
       * Raw host from {@link Office.onReady}. `null` when Office.js is
       * running outside any Office application (e.g. the taskpane URL
       * opened in a standalone browser).
       */
      officeHostType: Office.HostType | null;
    }
  | {
      status: "timeout";
      host: null;
      platform: null;
      officeHostType: null;
    };

/**
 * Maximum time to wait for the office.js `<script>` to attach the
 * global `Office` object. The CDN script is loaded via `next/script`
 * with the default `afterInteractive` strategy, so it can lag a few
 * hundred ms behind React hydration on cold opens. 15s is a generous
 * upper bound that still surfaces a clear failure if the script never
 * loads (offline, blocked by AV, broken WebView, etc.).
 */
const OFFICE_GLOBAL_WAIT_MS = 15_000;

/**
 * Resolve once `Office` is defined and {@link Office.onReady} has
 * resolved.
 *
 * Two-stage wait:
 * 1. Poll for the `Office` global. With `afterInteractive` `<Script>`,
 *    the global may not exist yet when React effects first fire.
 * 2. Once defined, await `Office.onReady()` so the host bridge is
 *    fully initialized before any Office API is used (otherwise APIs
 *    like the MSAL NAA bridge throw "Office.js has not fully loaded").
 */
export async function waitForOfficeReady(): Promise<OfficeReadyInfo> {
  const start = Date.now();
  while (typeof Office === "undefined") {
    if (Date.now() - start > OFFICE_GLOBAL_WAIT_MS) {
      console.warn(
        `[Magnus Add-in] Office.js global never appeared after ${OFFICE_GLOBAL_WAIT_MS}ms`
      );
      return {
        status: "timeout",
        host: null,
        platform: null,
        officeHostType: null,
      };
    }
    await new Promise((resolve) => setTimeout(resolve, 50));
  }

  console.info("[Magnus Add-in] Waiting for Office.onReady");
  const info = await Office.onReady();
  console.info("[Magnus Add-in] Office is ready", {
    host: info.host,
    platform: info.platform,
  });

  // Outside an Office host, Office.js resolves with null host/platform — do
  // not use truthiness: `Office.HostType.Word` is enum value 0.
  const officeHostType =
    info.host === null || info.host === undefined ? null : info.host;
  const hostLabel =
    officeHostType === null
      ? null
      : (Office.HostType[officeHostType] ?? String(officeHostType));
  const platformLabel =
    info.platform === null || info.platform === undefined
      ? null
      : String(info.platform);

  return {
    status: "ready",
    host: hostLabel,
    platform: platformLabel,
    officeHostType,
  };
}

function officeHostMatchesSurface(
  officeHostType: Office.HostType,
  surface: OfficeAddinSurface
): boolean {
  switch (surface) {
    case "excel-add-in":
      return officeHostType === Office.HostType.Excel;
    case "outlook-add-in":
      return officeHostType === Office.HostType.Outlook;
    case "word-add-in":
      return officeHostType === Office.HostType.Word;
    default:
      return false;
  }
}

/**
 * Ensures the taskpane bootstrap URL was opened inside the Office host that
 * matches {@link surface}. Prevents `/office-addin/taskpane?surface=…`
 * from stamping `magnus-host` when pasted into a normal browser — Office.js
 * reports null hosts in that case (see Office.onReady remarks).
 */
export async function verifyOfficeTaskpaneSurface(
  surface: OfficeAddinSurface
): Promise<{ ok: true } | { ok: false; message: string }> {
  const ready = await waitForOfficeReady();
  if (ready.status === "timeout") {
    return {
      ok: false,
      message:
        "Could not load Office.js. Open this page from inside an Office host (Excel, Outlook, or Word), or reload the task pane.",
    };
  }
  if (ready.officeHostType === null) {
    return {
      ok: false,
      message:
        "This add-in URL only works inside a Microsoft Office host (Excel, Outlook, or Word). Open the host app and launch Magnus from the ribbon — opening this link in a standalone browser cannot load the add-in.",
    };
  }
  if (!officeHostMatchesSurface(ready.officeHostType, surface)) {
    return {
      ok: false,
      message:
        "This URL is for a different Office application than the one that loaded it. Check the manifest SourceLocation matches your host.",
    };
  }
  return { ok: true };
}

/**
 * Get the login hint from Office's auth context (available on Office-on-Web).
 *
 * The login hint can be forwarded to MSAL so the user does not need to
 * re-enter their email address during sign-in.
 */
export async function getOfficeLoginHint(): Promise<string | undefined> {
  try {
    if (typeof Office === "undefined" || !Office.auth) {
      return undefined;
    }
    const tokenResult = await Office.auth.getAccessToken({
      allowSignInPrompt: false,
      allowConsentPrompt: false,
    });
    // The Office SSO token is a JWT; decode the payload to get the
    // preferred_username or upn claim for use as a login hint.
    const parts = tokenResult.split(".");
    const encodedPayload = parts[1];
    if (!encodedPayload) {
      return undefined;
    }
    const payload = JSON.parse(atob(encodedPayload));
    return (
      (payload.preferred_username as string | undefined) ??
      (payload.upn as string | undefined)
    );
  } catch {
    // Office SSO is not available — this is expected on some platforms.
    return undefined;
  }
}
